import { removeProjectLabelAssignments } from '@features/projects/labels/project-labels.store';
import { createWithEqualityFn } from 'zustand/traditional';
import { IProjectInfo, Project, ProjectID } from '@shared/models/project.model';
import axios, {
  AxiosHeaders,
  AxiosRequestConfig,
  AxiosResponseHeaders,
} from 'axios';
import { devtools } from 'zustand/middleware';
import {
  IProjectState,
  IUpdateProjectOptions,
  ProjectsLookup,
} from './project-state.model';
import { validateProject } from '@shared/validation/project-validation';
import { updateResourceLocally } from '../store.helpers';
import { UpdateClientAction } from '../store.model';
import { projectToProjectInfo } from '@shared/helpers/project.helpers';
import { safeMartinezUnion } from '@shared/helpers/geometry/martinez-polygon-clipping-ops.helpers';
import { useFolderStateStore } from '../folder/folder.store';
import { shallow } from 'zustand/shallow';
import { isBeforeDate, toDateString } from '@shared/helpers/date.helpers';
import { getConfig } from '../../providers/ConfigProvider';
import { getHmrStoreState, initHmrStore } from '../../helpers/vite.helpers';
import {
  isProjectFolder,
  isProjectInfo,
} from '@shared/helpers/project-folder.helpers';
import { isEqualIds } from '@shared/helpers/utils.helpers';
import { required } from '@shared/helpers/function.helpers';
import { isPrimitive } from '@shared/helpers/object.helpers';
import { createBuilding } from '@shared/helpers/project-factory.helpers';
import { migrateProposalsInProject } from '@shared/helpers/proposal-migration.helpers';
import { omitEmbeddedRecipesFromProject } from '@shared/helpers/omit-embedded-recipes.helpers';
import { AxiosRequestQueue } from '../helpers/request-queue';
import { guards } from '@shared/helpers/typeguards.helpers';
import { mergeFetchedProjects, projectIds } from './projects-lookup.helpers';
import {
  collectBlueprintImageDataUrls,
  preserveBlueprintImagesFromPrevious,
} from '@shared/helpers/design/blueprint-underlay-transfer.helpers';
import { rememberBlueprintRasters } from '@features/design/helpers/blueprint-underlay-raster.cache';
import { hasInFlightCancellableEnrichment } from '@client/workers/enrich-in-flight.helpers';
import { shouldPreserveLoadedProjectOnRefetchFailure } from '../../helpers/resource-load.helpers';

const STORE_NAME = 'project';

let latestProjectsFetchGeneration = 0;

const REQUEST_CONFIG: AxiosRequestConfig = {
  transformResponse: (
    data: unknown,
    headers: AxiosResponseHeaders,
    status?: number,
  ): Record<ProjectID, Project> | undefined => {
    if (!status || status >= 400 || !guards.is.string(data)) return undefined;
    return JSON.parse(data) as Record<ProjectID, Project>;
  },
} as const;

/**
 * Queue to avoid race conditions when updating project.
 * It will queue multiple updates and send them one by one to the server.
 */
const updateProjectRequestQueue = new AxiosRequestQueue<
  Project['updated_at'],
  { project: Project }
>({
  axiosConfig: REQUEST_CONFIG,
});

/**
 * True while a project PUT is queued or a newer enrich is still running.
 * An older PUT must not clear the spinner out from under that enrich.
 */
const isProjectWritePending = (): boolean =>
  updateProjectRequestQueue.hasQueuedItems() ||
  hasInFlightCancellableEnrichment();

/**
 * Drain the project PUT queue, rejecting any in-flight/queued requests.
 * Used by the 422 race-condition recovery to stop a cascade of stale sibling
 * PUTs before refetching and replaying on fresh server data.
 */
export const clearProjectUpdateQueue = (): void =>
  updateProjectRequestQueue.clearQueue();

/**
 * Wait until no project PUT is in flight or queued.
 * Geometry import reads `updated_at` after this so it does not share a token
 * with a persist that never took the design lock.
 */
export const waitForProjectUpdateQueueIdle = (): Promise<void> =>
  updateProjectRequestQueue.waitUntilIdle();

/**
 * True while a project PUT is in flight or waiting behind one.
 * Distinct from the spinner check, which ignores the in-flight item so the
 * last PUT can clear it from its own continuation.
 */
export const isProjectUpdateQueueBusy = (): boolean =>
  updateProjectRequestQueue.isLoading();

const defaultBuilding = createBuilding();
const DEFAULT_PROJECT: Project = {
  id: 0,
  owner: '_',
  organizations: ['_'],
  sharing_key: '',
  name: '',
  baselines: defaultBuilding.baselines,
  design: defaultBuilding.design,
  meta: defaultBuilding.meta,
  features: defaultBuilding.features,
  template: false,
  active_version_id: required(defaultBuilding.baselines[0]?.id),
  created_at: toDateString('2023-01-01'),
  updated_at: toDateString('2023-01-01'),
};

/**
 * Store to hold project related values.
 * Avoid using directly. Use the useProjectStore hook instead.
 */
export const useProjectStateStore = createWithEqualityFn<IProjectState>()(
  devtools<IProjectState>(
    (set, get) => ({
      isDeleting: false,
      isCreating: false,
      isLoading: false,
      isUpdating: false,
      projectsFetched: false,
      projectsFetching: false,
      publicProjectsFetching: false,
      publicProjectsFetched: false,
      projectsLookup: {},
      publicProjectsLookup: {},
      projectsOrg: undefined,
      project: DEFAULT_PROJECT,
      projectPromise: Promise.resolve(DEFAULT_PROJECT),
      projectTarget: { type: 'none' },
      // HMR sometimes causes multiple reloads of this file. Keep store state between reloads
      ...getHmrStoreState(STORE_NAME),

      fetchPublicProjects: async () => {
        set(() => ({
          publicProjectsFetching: true,
          publicProjectsFetched: false,
          publicProjectsError: undefined,
        }));

        try {
          const { data } = await axios.get<ProjectsLookup>(`projects/public`);

          set(() => ({
            publicProjectsFetching: false,
            publicProjectsFetched: true,
            publicProjectsError: undefined,
            publicProjectsLookup: data,
          }));

          return data;
        } catch (err) {
          const error = guards.is.error(err) ? err : new Error(String(err));
          set(() => ({
            publicProjectsFetching: false,
            publicProjectsFetched: false,
            publicProjectsError: error,
          }));
          throw error;
        }
      },

      fetchProjects: async (orgOverride?: string) => {
        const fetchGeneration = ++latestProjectsFetchGeneration;
        const idsWhenFetchStarted = projectIds(get().projectsLookup);
        const previous = get();
        // Keep the last successful list if this is a refetch (HMR remount). A
        // failed refetch must not look like "no projects" and send us to 404.
        const isSameOrgRefetch =
          previous.projectsFetched &&
          previous.projectsOrg !== undefined &&
          (!orgOverride || previous.projectsOrg === orgOverride);

        set(() => ({
          projectsFetching: true,
          projectsFetched: isSameOrgRefetch,
          error: undefined,
          projectsError: undefined,
        }));

        try {
          const headers = new AxiosHeaders();
          if (orgOverride) headers.set('Organization', orgOverride);
          const { data, config } = await axios.get<ProjectsLookup>(`projects`, {
            ...REQUEST_CONFIG,
            headers: headers,
          });

          const isStaleFetch =
            fetchGeneration !== latestProjectsFetchGeneration;
          if (isStaleFetch) return get().projectsLookup;

          const projectsLookup = mergeFetchedProjects(
            data,
            get().projectsLookup,
            idsWhenFetchStarted,
          );

          set(() => ({
            projectsFetching: false,
            projectsFetched: true,
            error: undefined,
            projectsError: undefined,
            projectsLookup,
            projectsOrg: config.headers.Organization,
          }));
          return projectsLookup;
        } catch (err) {
          const error = guards.is.error(err) ? err : new Error(String(err));
          if (fetchGeneration === latestProjectsFetchGeneration) {
            set(() => ({
              projectsFetching: false,
              projectsFetched: isSameOrgRefetch,
              projectsError: isSameOrgRefetch ? undefined : error,
              error: isSameOrgRefetch ? undefined : error,
            }));
          }
          throw error;
        }
      },

      fetchProject: async (id, orgOverride, publishAfter) => {
        const {
          projectTarget: target,
          project,
          projectPromise,
          isLoading,
        } = get();
        // If the project is already in the store, return it
        if (target.type === 'id' && target.id === id) {
          if (project.id === id) {
            if (get().projectError) {
              set(() => ({ projectError: undefined, error: undefined }));
            }
            return project;
          }
          if (isLoading) return await projectPromise;
        }

        try {
          const projectPromise = fetchProject(id, orgOverride);
          set(() => ({
            isLoading: true,
            projectPromise,
            projectTarget: { type: 'id', id },
            projectError: undefined,
            error: undefined,
          }));
          const project = await projectPromise;
          // Publishing triggers project consumers and enrichment; keep it off
          // the list camera's animation frames while the request runs in parallel.
          if (publishAfter) await publishAfter;

          set(() => ({
            project,
            projectPromise,
            isLoading: false,
            projectError: undefined,
          }));

          return project;
        } catch (err) {
          const error = guards.is.error(err) ? err : new Error(String(err));
          // HMR remounts must not 404 an already-open project; production still does.
          const hasLoadedProject =
            get().project.id === id &&
            shouldPreserveLoadedProjectOnRefetchFailure();
          set(() => ({
            isLoading: false,
            error: hasLoadedProject ? undefined : error,
            projectError: hasLoadedProject ? undefined : error,
          }));
          throw error;
        }
      },
      fetchPublicProject: async (sharing_key) => {
        const { projectTarget: target, project, projectPromise } = get();
        if (target.type === 'shared' && target.key === sharing_key) {
          return project.id !== 0 ? project : await projectPromise;
        }
        try {
          const config = await getConfig();
          const projectPromise = fetchPublicProject(
            sharing_key,
            config.version,
          );
          set(() => ({
            isLoading: true,
            projectPromise,
            projectTarget: { type: 'shared', key: sharing_key },
          }));
          const project = await projectPromise;
          set(() => ({
            project,
            isLoading: false,
            projectError: undefined,
            projectsLookup: {
              [project.id]: projectToProjectInfo(project, safeMartinezUnion),
            },
          }));

          return project;
        } catch (err) {
          const error = guards.is.error(err) ? err : new Error(String(err));
          set(() => ({ isLoading: false, projectError: error }));
          return undefined;
        }
      },
      fetchGenerationProject: async (generation_key) => {
        const { projectTarget: target, project, projectPromise } = get();
        if (target.type === 'generation' && target.key === generation_key) {
          return project.id !== 0 ? project : await projectPromise;
        }
        try {
          const config = await getConfig();
          const projectPromise = fetchGenerationProject(
            generation_key,
            config.version,
          );
          set(() => ({
            isLoading: true,
            projectPromise,
            projectTarget: { type: 'generation', key: generation_key },
          }));
          const project = await projectPromise;
          set(() => ({
            project,
            isLoading: false,
            projectError: undefined,
            projectsLookup: {
              [project.id]: projectToProjectInfo(project, safeMartinezUnion),
            },
          }));

          return project;
        } catch (err) {
          const error = guards.is.error(err) ? err : new Error(String(err));
          set(() => ({ isLoading: false, projectError: error }));
          return undefined;
        }
      },
      createProject: async (payload, orgOverride) => {
        const { isCreating, updateProjectLocally } = get();
        const headers = new AxiosHeaders();
        if (orgOverride) headers.set('Organization', orgOverride);
        if (isCreating) throw new Error('Already creating project');
        set(() => ({ isCreating: true }));

        try {
          const { data } = await axios.post<Project>('projects', payload, {
            ...REQUEST_CONFIG,
            headers: headers,
          });

          updateProjectLocally({
            action: UpdateClientAction.Add,
            itemOrId: data,
          });

          return data;
        } catch (err) {
          set(() => ({ isCreating: false }));
          throw err;
        }
      },

      deleteProject: async (id) => {
        const { updateProjectLocally } = get();

        set(() => ({ isDeleting: true }));

        try {
          const { data: deletedId } = await axios.delete<ProjectID>(
            `/projects/${id}`,
          );
          removeProjectLabelAssignments(String(id));
          updateProjectLocally({
            action: UpdateClientAction.Remove,
            itemOrId: String(id),
            options: { isDeleting: false },
          });

          return deletedId;
        } catch (err) {
          set(() => ({ isDeleting: false }));
          return Promise.reject(err);
        }
      },

      updateProject: async (
        updatedProject: Project,
        options: IUpdateProjectOptions = {},
      ): Promise<Project> => {
        const { project, updateProjectLocally } = get();
        const { id } = project;
        const serverOnly = options.serverOnly ?? false;

        if (id !== updatedProject.id) {
          throw new Error('Project ID mismatch');
        }

        if (serverOnly) {
          if (project !== updatedProject) {
            throw new Error(
              'serverOnly requires the live store project reference to match the payload (commit locally first)',
            );
          }
        } else {
          // Don't send to server if nothing has changed
          if (project === updatedProject) {
            return project;
          }

          // Set project in store without waiting for server response
          updatedProject = updateProjectLocally({
            action: UpdateClientAction.Update,
            itemOrId: updatedProject,
            options: {
              isLoading: !options.skipServerUpdate, // Should isLoading be toggled here?
              isUpdating: !options.skipServerUpdate,
            },
          });
        }

        // Don't send to server if only local update is requested
        if (options.skipServerUpdate) {
          options.onPersisted?.();
          return updatedProject;
        }

        try {
          // Align timestamp with the live store (and queued puts) so a prior save or
          // socket update does not reject this payload with a race-condition error.
          const storeProject = get().project;
          if (
            updatedProject.id === storeProject.id &&
            isBeforeDate(updatedProject.updated_at, storeProject.updated_at)
          ) {
            updatedProject.updated_at = storeProject.updated_at;
          }

          // Use updated_at from server to ensure correct timestamps.
          // effectsOutsideUndo is client-only undo-stack state — never persisted.
          const { data: updated_at } = await updateProjectRequestQueue.put(
            { project: { ...updatedProject, effectsOutsideUndo: undefined } },
            `projects`,
          );

          // `isLoading()` is still true here — this PUT is `processingItem` until
          // the queue yields. Remaining queued PUTs, or a newer enrich, keep
          // the AppBar spinner.
          const queueLoading = isProjectWritePending();
          set(() => ({
            isLoading: queueLoading,
            isUpdating: queueLoading,
          }));

          // Set updated_at without triggering rerenders of views depending on project
          const projectInfo = required(get().projectsLookup[id]);
          silentlySetUpdatedAt(projectInfo, updated_at);

          options.onPersisted?.();
          return silentlySetUpdatedAt(updatedProject, updated_at);
        } catch (err) {
          const queueLoading = isProjectWritePending();
          const live = get().project;
          // serverOnly leaves the store as `project`. Other saves replace it
          // first, and `updatedProject` is the object now in the store.
          const thisSaveProject = serverOnly ? project : updatedProject;
          // A newer edit keeps this timestamp until its own PUT returns
          // (DEV-3785). Same updated_at is not proof this save still owns the store.
          const stillThisSave =
            live === thisSaveProject && live.updated_at === project.updated_at;

          if (stillThisSave) {
            updateProjectLocally({
              action: UpdateClientAction.Update,
              itemOrId: project,
              options: {
                isLoading: queueLoading,
                isUpdating: queueLoading,
              },
            });
          } else {
            // Socket, or a newer edit, already replaced this snapshot.
            set(() => ({
              isLoading: queueLoading,
              isUpdating: queueLoading,
            }));
          }
          return Promise.reject(err);
        }
      },

      updateProjectDetails: async (payload) => {
        const { updateProjectLocally } = get();

        set(() => ({ isLoading: true, isUpdating: true }));

        try {
          const { data: updatedProject } = await axios.put<Project>(
            `projects/details`,
            payload,
            REQUEST_CONFIG,
          );
          updateProjectLocally({
            action: UpdateClientAction.Update,
            itemOrId: updatedProject,
            options: { isLoading: false },
          });
          return updatedProject;
        } catch (err) {
          set(() => ({ isLoading: false, isUpdating: false }));
          return Promise.reject(err);
        }
      },

      updateProjectAndFolderLocations: async (items) => {
        const { project } = get();

        const projects = items.filter(isProjectInfo);
        const folders = items.filter(isProjectFolder);

        const changedProject = projects.find(({ id }) =>
          isEqualIds(id, project),
        );

        // TODO: refactor to use updateProjectLocally, if possible
        set(({ projectsLookup }) => {
          return {
            isLoading: true,
            project: changedProject
              ? {
                  ...project,
                  parent_id: changedProject.parent_id,
                  location: changedProject.location,
                }
              : project,
            projectsLookup: {
              ...projectsLookup,
              ...projects.reduce(
                (acc, updatedProject) => ({
                  ...acc,
                  [updatedProject.id]: updatedProject,
                }),
                {} as ProjectsLookup,
              ),
            },
          };
        });

        const folderState = useFolderStateStore.getState();
        folderState.updateFoldersLocally(folders);

        await axios.put(
          `/projects/locations`,
          { projectInfos: projects, folders },
          REQUEST_CONFIG,
        );

        set(() => ({ isLoading: false }));
      },

      updateProjectLocally: ({
        action,
        itemOrId,
        options: {
          isCreating = false,
          isDeleting = false,
          isLoading = false,
          isUpdating = false,
        } = {},
      }): Project => {
        const { project } = get();
        // When deleting only id is provided, update/add will have a project object
        let updatedProject = isPrimitive(itemOrId) ? project : itemOrId;

        // id of the project being updated
        const updatedProjectId = isPrimitive(itemOrId) ? itemOrId : itemOrId.id;
        // id of the project in the store
        const currentStoreProjectId = project.id;

        // Stale-socket guard applies only to the open project — not to adding a
        // different project to the list (create would otherwise be dropped when
        // the open project's updated_at is newer than the created row).
        // Geometry-import token sync relies on this: an older GET snapshot must
        // not replace a newer in-memory project.
        if (
          updatedProjectId === currentStoreProjectId &&
          isBeforeDate(updatedProject.updated_at, project.updated_at)
        ) {
          set(() => ({
            isCreating,
            isDeleting,
            isLoading,
            isUpdating,
          }));
          return project;
        }

        // Socket ProjectUpdate strips rasters; keep JPEGs in the client cache only.
        if (
          !isPrimitive(itemOrId) &&
          action === UpdateClientAction.Update &&
          project.id === itemOrId.id
        ) {
          rememberBlueprintRasters(collectBlueprintImageDataUrls(project));
          rememberBlueprintRasters(
            collectBlueprintImageDataUrls(updatedProject),
          );
          updatedProject = preserveBlueprintImagesFromPrevious(
            project,
            updatedProject,
          );
        }

        let { projectsLookup } = get();

        if (
          action === UpdateClientAction.Add ||
          action === UpdateClientAction.Update
        ) {
          // Don't send to server if not changed
          if (project === itemOrId) {
            return project;
          }

          const { lookup } = updateResourceLocally({
            action,
            itemOrId: projectToProjectInfo(itemOrId, safeMartinezUnion),
            lookup: projectsLookup,
          });

          projectsLookup = lookup ?? projectsLookup;
        }

        if (action === UpdateClientAction.Remove) {
          const { lookup } = updateResourceLocally<IProjectInfo>({
            action,
            itemOrId,
            lookup: projectsLookup,
          });

          projectsLookup = lookup ?? projectsLookup;
        }

        // Validate enriched snapshots only (optimistic updates use enriched: false until
        // enrichProject finishes; see validateProject at end of enrichProject).
        // Remove actions can target corrupted legacy projects and should still succeed.
        if (
          action !== UpdateClientAction.Remove &&
          updatedProject.enriched !== false
        ) {
          // skipEnrichment writes never run the worker, so leftover
          // baselines[].recipes would fail validation (DEV-3491 / DEV-3701).
          updatedProject = omitEmbeddedRecipesFromProject(updatedProject);
          validateProject(updatedProject);
        }

        set(() => ({
          isCreating,
          isDeleting,
          isLoading,
          isUpdating,
          projectsLookup,
          project:
            updatedProjectId === currentStoreProjectId // we only want to set the project if it's the same project as the one in the store. Otherwise the URL will be out of sync with the store project.
              ? updatedProject
              : project,
        }));

        return updatedProject;
      },
    }),
    { name: STORE_NAME },
  ),
  shallow,
);

/**
 * Fetch project from server. Will not cause state change in project store.
 * Runs `migrateProposalsInProject` on the payload so legacy proposals are migrated on load.
 *
 * @param id
 * @returns
 */
export const fetchProject = async (id: number, orgOverride?: string) => {
  const headers = new AxiosHeaders();
  if (orgOverride) headers.set('Organization', orgOverride);
  const { data } = await axios.get<Project>(`projects/${id}`, {
    ...REQUEST_CONFIG,
    headers: headers,
  });

  // Read-time proposal migration (legacy v1 → action-backed rows); TODO remove when all projects are migrated.
  return migrateProposalsInProject(data);
};

/**
 * Fetch public project from server. Runs `migrateProposalsInProject` on the payload.
 * @param id
 * @returns
 */
export const fetchPublicProject = async (
  sharingKey: string,
  configVersion: string,
) => {
  const { data } = await axios.get<Project>(`projects/shared/${sharingKey}`, {
    ...REQUEST_CONFIG,
    headers: {
      AppVersion: configVersion,
    },
  });

  return migrateProposalsInProject(data);
};

/**
 * Loads a project by time-limited PDF {@link generation_key} (server-only, not user-facing).
 */
export const fetchGenerationProject = async (
  generationKey: string,
  configVersion: string,
) => {
  const { data } = await axios.get<Project>(
    `projects/generation/${generationKey}`,
    {
      ...REQUEST_CONFIG,
      headers: {
        AppVersion: configVersion,
      },
    },
  );

  return migrateProposalsInProject(data);
};

/**
 * Set project.updated_at without triggering rerenders of views depending on project.
 * DO NOT USE THIS FUNCTION FOR OTHER PURPOSES OR OUTSIDE OF THIS STORE.
 * @param project
 * @param updated_at
 */
const silentlySetUpdatedAt = <T extends Project | IProjectInfo>(
  project: T,
  updated_at: string,
): T => {
  project.updated_at = updated_at;

  // When the store project has been replaced by an optimistic update (different
  // reference, same id), propagate the timestamp so that subsequent calls
  // (including web-worker results) pick up the latest server timestamp.
  const storeProject = useProjectStateStore.getState().project;
  if (storeProject !== project && storeProject.id === project.id) {
    storeProject.updated_at = updated_at;
  }

  // Apply on queue as well to enable us to queue muliple projects changes without server rejecting it
  updateProjectRequestQueue.getQueueData().forEach((data) => {
    if (data.project.id === project.id) {
      data.project.updated_at = updated_at;
    }
  });

  return project;
};

// In HMR mode we need to keep the store state between reloads
initHmrStore(STORE_NAME, useProjectStateStore);
